import { Controller } from '@hotwired/stimulus';
import AutoNumeric from 'autonumeric';

export default class TextInput extends Controller {
  static targets = ['input', 'inputGroup', 'errorText', 'requiredMark'];

  isCurrency = false;
  initialValue = null;
  fullInputName = null;
  formattedInputName = null;
  inputName = null;

  static events = {
    ready: 'text-input-ready'
  };

  connect() {
    // format currency fields on input and blur
    if (!this.hasInputTarget) return;
    this.initialValue = this.currentValue;
    if (this.inputTarget.dataset?.type === 'currency') {
      const anSettings = {
        'minimumValue': '0',
        'negativeSignCharacter': ' ',
        'dotDecimalCharCommaSeparator': '',
        'modifyValueOnWheel': false
      };

      // lazy init AutoNumeric on first focus
      this.inputTarget.addEventListener('focus', () => {
        if (!this.an) {
          this.an = new AutoNumeric(this.inputTarget, anSettings);
          this.isCurrency = true;
        }
      }, { once: true });
    }

    // Grab different name variants for different use cases
    // Uneditied html name, if input is nested, will contain brackets, eg. product[assignments][enabled]
    this.fullInputName = this.inputTarget.name;
    // Formatted name, replacing brackets with dots, eg. product.assignments.enabled
    this.formattedInputName = this.fullInputName.replace(/\[([^\]]+)\]/g, '.$1');
    // Simple name of the input, only grab the human name for simple cases, eg. enabled
    this.inputName = this.formattedInputName.includes('.') ?
      this.formattedInputName.split('.').at(-1) :
      this.formattedInputName;

    const eventDetail = { input: this };
    this.inputTarget.dispatchEvent(new CustomEvent(TextInput.events.ready, { detail: eventDetail }));
  }

  get currentValue() {
    return this.inputTarget.value;
  }

  set currentValue(value) {
    if (this.isCurrency) this.an.set(value);
    else this.inputTarget.value = value;
  }

  get name() {
    return this.inputName || this.inputTarget.name;
  }

  resetValue(disable = false, clearError = false) {
    this.currentValue = this.initialValue;
    if (disable) this.disable();
    if (clearError) this.clearError();
  }

  setRequired(required) {
    if (required) {
      this.inputTarget.setAttribute('required', true);
      if (this.hasRequiredMarkTarget) {
        this.requiredMarkTarget.classList.remove('hidden');
      }
    } else {
      this.inputTarget.removeAttribute('required');
      if (this.hasRequiredMarkTarget) {
        this.requiredMarkTarget.classList.add('hidden');
      }
    }
  }

  setError(message) {
    this.inputGroupTarget.classList.add('error');

    if (message.includes('contact support')) {
      // Replace plain text w/ link
      const tmp = document.createElement('div');
      tmp.innerHTML = message.replace(
        'contact support.',
        '<a href="/contact-support" class="body-semi-bold underline-hover">contact support.</a>'
      );
      this.errorTextTarget.innerHTML = tmp.innerHTML;
    } else {
      this.errorTextTarget.textContent = message;
    }

    // Prevent push from empty message
    if (!message) this.errorTextTarget.classList.add('hidden');
    else this.errorTextTarget.classList.remove('hidden');
  }

  clearError() {
    this.inputGroupTarget.classList.remove('error');
    this.errorTextTarget.textContent = '';
  }

  disable() {
    this.inputGroupTarget.classList.add('disabled');
    this.inputTarget.setAttribute('disabled', true);
  }

  enable() {
    this.inputGroupTarget.classList.remove('disabled');
    this.inputTarget.removeAttribute('disabled');
  }

  show() {
    this.inputGroupTarget.classList.remove('hidden');
    this.inputTarget.removeAttribute('hidden');
  }

  hide() {
    this.inputGroupTarget.classList.add('hidden');
    this.inputTarget.setAttribute('hidden', true);
  }

  validate(showError = true) {
    const input = this.inputTarget;
    const value = input.value.trim();
    const customErrorMsg = this.inputGroupTarget.dataset.errorText;
    let valid = true;
    let errorMsg = '';

    // Validate required field, set error and ignore remaining validation
    if (input.required && !value) {
      // Prevent input bouncing when custom error is an empty string
      if (showError) {
        if (customErrorMsg === '') this.setError('');
        else this.setError(customErrorMsg || 'Input required');
      }
      return false;
    }

    // If no value and input is not required, skip other validation
    if (!value && !input.required) return valid;

    // Validate special fields
    switch (input.dataset.type) {
      case 'email':
        if (!this.validateEmail(value)) {
          errorMsg = 'Please enter a valid email or contact support.';
          valid = false;
        }
        break;
      case 'phone':
        if (!this.validatePhone(value)) {
          errorMsg = 'Invalid phone number';
          valid = false;
        }
        break;
      case 'zip':
        if (!this.validateZip(value)) {
          errorMsg = 'Invalid zip code';
          valid = false;
        }
        break;
      case 'url':
        if (!this.validateUrl(value)) {
          errorMsg = 'Invalid URL';
          valid = false;
        }
      default:
        break;
    }

    // Set error and allow empty custom errors
    if (!valid && showError) {
      this.setError(typeof customErrorMsg === 'undefined' ? errorMsg : customErrorMsg);
    } else this.clearError();

    return valid;
  }

  validatePhone(value) {
    const cleanedPhoneNumber = value.replace(/\D/g, '');
    return cleanedPhoneNumber.length === 10;
  }

  validateEmail(value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }

  validateZip(value) {
    const regex = /^\d{5}$/;
    return regex.test(value);
  }

  formatPhoneNumber(value) {
    const cleanedPhoneNumber = value.replace(/\D/g, '');
    const phoneNumberLength = cleanedPhoneNumber.length;

    if (phoneNumberLength < 4) return cleanedPhoneNumber;
    if (phoneNumberLength < 7) {
      return `(${cleanedPhoneNumber.slice(0, 3)}) ${cleanedPhoneNumber.slice(3)}`;
    }

    const areaCode = `(${cleanedPhoneNumber.slice(0, 3)})`;
    return `${areaCode} ${cleanedPhoneNumber.slice(3, 6)}-${cleanedPhoneNumber.slice(6, 10)}`;
  }

  handlePhoneInput() {
    const formattedValue = this.formatPhoneNumber(this.inputTarget.value);
    this.inputTarget.value = formattedValue;
  }

  handleNumberInput(e) {
    this.inputTarget.value = e.target.value.replace(/[^0-9]/g, '');
  }

  togglePassword(e) {
    const isHidden = this.inputTarget.type === 'password';
    this.inputTarget.type = isHidden ? 'text' : 'password';
    e.target.classList.remove(isHidden ? 'masked' : 'unmasked');
    e.target.classList.add(isHidden ? 'unmasked' : 'masked');
  }
  
  validateUrl(value) {
    // this should allow common urls
    // not too strict. accepts real world links
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[^\s]*)?$/i;
    return urlRegex.test(value);
  }
}
